import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, Alert, Card, CardContent, CardHeader,
  Grid, IconButton, Stack, CircularProgress
} from '@mui/material';
import {
  Save as SaveIcon, Add as AddIcon, Delete as DeleteIcon
} from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from '../services/api';

const QUILL_MODULES = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    ['clean'],
  ],
};

const QUILL_FORMATS = ['header', 'bold', 'italic', 'underline', 'list', 'bullet'];

const RAMSConfig = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Director fields
  const [director, setDirector] = useState('');
  const [directorPhone, setDirectorPhone] = useState('');
  const [directorEmail, setDirectorEmail] = useState('');

  // Emergency contacts
  const [emergencyContacts, setEmergencyContacts] = useState([]);

  // Rich text fields
  const [generalPrecautions, setGeneralPrecautions] = useState('');
  const [staffInfoSheet, setStaffInfoSheet] = useState('');

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await api.get('/tenant/branding');
      const branding = response.data.branding;

      setDirector(branding.director || '');
      setDirectorPhone(branding.director_phone || '');
      setDirectorEmail(branding.director_email || '');

      const contacts = branding.rams_emergency_contacts;
      if (contacts && Array.isArray(contacts) && contacts.length > 0) {
        setEmergencyContacts(contacts);
      } else {
        setEmergencyContacts([]);
      }

      setGeneralPrecautions(branding.rams_general_precautions || '');
      setStaffInfoSheet(branding.rams_staff_info_sheet || '');
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load RAMS configuration' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      await api.put('/tenant/branding', {
        director,
        director_phone: directorPhone,
        director_email: directorEmail,
        rams_emergency_contacts: emergencyContacts,
        rams_general_precautions: generalPrecautions,
        rams_staff_info_sheet: staffInfoSheet,
      });

      setMessage({ type: 'success', text: 'RAMS configuration saved successfully!' });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to save RAMS configuration'
      });
    } finally {
      setSaving(false);
    }
  };

  const updateContact = useCallback((index, field, value) => {
    setEmergencyContacts(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  }, []);

  const addContact = useCallback(() => {
    setEmergencyContacts(prev => [...prev, { service: '', number: '' }]);
  }, []);

  const removeContact = useCallback((index) => {
    setEmergencyContacts(prev => prev.filter((_, i) => i !== index));
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {message.text && (
        <Alert severity={message.type} onClose={() => setMessage({ type: '', text: '' })} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure default content for generated RAMS PDFs. These settings apply to all
        new RAMS documents. Fields left blank will use built-in defaults.
      </Typography>

      {/* Director Information */}
      <Card sx={{ mb: 3 }}>
        <CardHeader title="Director" titleTypographyProps={{ variant: 'subtitle1', fontWeight: 'bold' }} />
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth size="small" label="Director Name"
                value={director} onChange={(e) => setDirector(e.target.value)}
                placeholder="Director name"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth size="small" label="Director Phone"
                value={directorPhone} onChange={(e) => setDirectorPhone(e.target.value)}
                placeholder="Phone number"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth size="small" label="Director Email"
                value={directorEmail} onChange={(e) => setDirectorEmail(e.target.value)}
                placeholder="Email address"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Emergency Contact Numbers */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Emergency Contact Numbers"
          titleTypographyProps={{ variant: 'subtitle1', fontWeight: 'bold' }}
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" startIcon={<AddIcon />} onClick={addContact} variant="outlined">
                Add
              </Button>
            </Box>
          }
        />
        <CardContent>
          <Stack spacing={1}>
            {emergencyContacts.map((contact, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  size="small" label="Service" value={contact.service}
                  onChange={(e) => updateContact(index, 'service', e.target.value)}
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small" label="Number" value={contact.number}
                  onChange={(e) => updateContact(index, 'number', e.target.value)}
                  sx={{ flex: 1 }}
                />
                <IconButton size="small" onClick={() => removeContact(index)} color="error">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            {emergencyContacts.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No emergency contacts configured.
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* General Precautions */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="General Precautions"
          titleTypographyProps={{ variant: 'subtitle1', fontWeight: 'bold' }}
          subheader="Leave blank to use built-in default precautions in the PDF"
        />
        <CardContent>
          <ReactQuill
            value={generalPrecautions}
            onChange={setGeneralPrecautions}
            modules={QUILL_MODULES}
            formats={QUILL_FORMATS}
            placeholder="Enter custom general precautions content..."
            style={{ minHeight: 200 }}
          />
        </CardContent>
      </Card>

      {/* Staff Information Sheet */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Staff Information Sheet"
          titleTypographyProps={{ variant: 'subtitle1', fontWeight: 'bold' }}
          subheader="Leave blank to use built-in default staff information in the PDF"
        />
        <CardContent>
          <ReactQuill
            value={staffInfoSheet}
            onChange={setStaffInfoSheet}
            modules={QUILL_MODULES}
            formats={QUILL_FORMATS}
            placeholder="Enter custom staff information sheet content..."
            style={{ minHeight: 200 }}
          />
        </CardContent>
      </Card>

      {/* Save Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save RAMS Configuration'}
        </Button>
      </Box>
    </Box>
  );
};

export default RAMSConfig;
